import { getBackgroundColorClass } from "@/lib/colors";
import type React from "react";
import { renderTimeAgo } from "@/components/TimeAgo";
import type { TriggerEventContext, TriggerRenderer, TriggerRendererContext } from "../types";
import type { TriggerProps } from "@/ui/trigger";
import firehydrantIcon from "@/assets/icons/integrations/firehydrant.svg";
import type { Incident } from "./types";
import { getDetailsForIncident } from "./base";

interface OnIncidentEventData {
  event?: string;
  incident?: Incident;
}

export const onIncidentTriggerRenderer: TriggerRenderer = {
  getTitleAndSubtitle: (context: TriggerEventContext): { title: string; subtitle: string | React.ReactNode } => {
    const eventData = context.event?.data as OnIncidentEventData;
    const incident = eventData?.incident;
    const contentParts = [incident?.severity, incident?.current_milestone].filter(Boolean).join(" · ");
    const subtitle = buildSubtitle(contentParts, context.event?.createdAt);

    return {
      title: incident?.name || "Incident",
      subtitle,
    };
  },

  getRootEventValues: (context: TriggerEventContext): Record<string, string> => {
    const eventData = context.event?.data as OnIncidentEventData;
    return getDetailsForIncident(eventData?.incident);
  },

  getTriggerProps: (context: TriggerRendererContext) => {
    const { node, definition, lastEvent } = context;
    const configuration = node.configuration as { severities?: string[]; current_milestone?: string[] };
    const metadataItems = [];

    if (configuration?.severities?.length) {
      const formattedSeverities = configuration.severities.join(", ");
      metadataItems.push({
        icon: "funnel",
        label: "Severities: " + formattedSeverities,
      });
    }

    if (configuration?.current_milestone?.length) {
      const formattedMilestones = configuration.current_milestone.join(", ");
      metadataItems.push({
        icon: "funnel",
        label: "Milestones: " + formattedMilestones,
      });
    }

    const props: TriggerProps = {
      title: node.name!,
      iconSrc: firehydrantIcon,
      collapsedBackground: getBackgroundColorClass(definition.color),
      metadata: metadataItems,
    };

    if (lastEvent) {
      const eventData = lastEvent.data as OnIncidentEventData;
      const incident = eventData?.incident;
      const contentParts = [incident?.severity, incident?.current_milestone].filter(Boolean).join(" · ");
      const subtitle = buildSubtitle(contentParts, lastEvent.createdAt);

      props.lastEventData = {
        title: incident?.name || "Incident",
        subtitle,
        receivedAt: new Date(lastEvent.createdAt),
        state: "triggered",
        eventId: lastEvent.id,
      };
    }

    return props;
  },
};

function buildSubtitle(content: string, createdAt?: string): string | React.ReactNode {
  const timeAgo = createdAt ? renderTimeAgo(new Date(createdAt)) : "";
  if (content && timeAgo) {
    return content + " · " + timeAgo;
  }

  return content || timeAgo;
}
